/**
 * Edit Recurring Schedule Modal
 * Allows company administrators to edit existing recurring schedules
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { updateRecurringSchedule, type RecurringScheduleWithId } from '../../../services/recurringSchedule.service';
import type { RecurrenceFrequency } from '../../../../../shared/types';
import { ErrorMessage } from '../../ErrorMessage';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { BaseModal } from '../../common/BaseModal';
import { FormInputField } from '../../form/FormInputField';
import { FormTextareaField } from '../../form/FormTextareaField';
import { FormSelectField } from '../../form/FormSelectField';
import { LocationAutocompleteField } from '../../form/LocationAutocompleteField';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { FORM_GROUP_STYLE, LABEL_STYLE, INPUT_STYLE } from '../../../styles/modalStyles';
import { DAYS_OF_WEEK, FREQUENCY_OPTIONS, DEFAULT_WORKING_DAYS, DEFAULT_SCHEDULE_TIMES } from '../../../constants/schedule';

const toDateInputString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

interface EditRecurringScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  schedule: RecurringScheduleWithId | null;
  onSuccess: () => void;
}

interface FormData {
  title: string;
  description: string;
  address: string;
  latitude: number;
  longitude: number;
  hourlyRate: number;
  recurrenceFrequency: RecurrenceFrequency;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  effectiveUntil: string;
}

export function EditRecurringScheduleModal({
  isOpen,
  onClose,
  schedule,
  onSuccess,
}: EditRecurringScheduleModalProps) {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    address: '',
    latitude: 0,
    longitude: 0,
    hourlyRate: 0,
    recurrenceFrequency: 'weekly',
    daysOfWeek: [...DEFAULT_WORKING_DAYS],
    startTime: DEFAULT_SCHEDULE_TIMES.startTime,
    endTime: DEFAULT_SCHEDULE_TIMES.endTime,
    effectiveUntil: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Populate form when schedule changes
  useEffect(() => {
    if (schedule) {
      setFormData({
        title: schedule.title,
        description: schedule.description || '',
        address: schedule.location.address,
        latitude: schedule.location.latitude,
        longitude: schedule.location.longitude,
        hourlyRate: schedule.hourlyRate,
        recurrenceFrequency: schedule.recurrenceFrequency,
        daysOfWeek: schedule.daysOfWeek,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        effectiveUntil: schedule.effectiveUntil
          ? toDateInputString(new Date(schedule.effectiveUntil))
          : '',
      });
      setError('');
    }
  }, [schedule]);

  const handleDayToggle = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!schedule) return;

    const data = formDataRef.current;

    // Validation
    if (!data.title.trim()) {
      setError('Schedule title is required');
      return;
    }

    if (!data.address.trim()) {
      setError('Location address is required');
      return;
    }

    if (data.latitude === 0 && data.longitude === 0) {
      setError('Please select an address from the suggestions to set GPS coordinates');
      return;
    }

    if (data.hourlyRate <= 0) {
      setError('Hourly rate must be greater than 0');
      return;
    }

    if (data.daysOfWeek.length === 0) {
      setError('At least one day must be selected');
      return;
    }

    if (data.startTime === data.endTime) {
      setError('Start time and end time cannot be the same');
      return;
    }

    // Warn if editing assigned schedule
    if (schedule.assignmentStatus !== 'unassigned') {
      const assignmentType = schedule.assignmentStatus === 'assigned_to_manager' ? 'a manager' : 'staff';
      setConfirmDialog({
        title: 'Edit Assigned Schedule',
        message: `This schedule is currently assigned to ${assignmentType}. Changes may affect existing job assignments.`,
        onConfirm: () => { setConfirmDialog(null); performUpdate(data); },
      });
      return;
    }

    performUpdate(data);
  };

  const performUpdate = async (data: FormData) => {
    if (!schedule) return;
    setLoading(true);

    try {
      await updateRecurringSchedule({
        scheduleId: schedule.id,
        companyId: schedule.companyId,
        title: data.title.trim(),
        description: data.description.trim() || undefined,
        location: {
          address: data.address.trim(),
          latitude: data.latitude,
          longitude: data.longitude,
        },
        hourlyRate: data.hourlyRate,
        recurrenceFrequency: data.recurrenceFrequency,
        daysOfWeek: data.daysOfWeek,
        startTime: data.startTime,
        endTime: data.endTime,
        effectiveUntil: data.effectiveUntil ? new Date(data.effectiveUntil) : null,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update recurring schedule');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !schedule) return null;

  return (
    <>
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Recurring Schedule"
      size="medium"
      loading={loading}
    >
      <form onSubmit={handleSubmit}>
        <FormInputField
          label="Schedule Title"
          value={formData.title}
          onChange={(value) => setFormData({ ...formData, title: value })}
          placeholder="e.g., Morning Shift Security"
          required
        />

        <FormTextareaField
          label="Description"
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: value })}
          placeholder="Job details and requirements..."
          rows={2}
        />

        <LocationAutocompleteField
          key={schedule?.id}
          label="Location Address"
          initialValue={schedule?.location.address}
          onChange={(result) => setFormData((prev) => ({
            ...prev,
            address: result.address,
            latitude: result.latitude,
            longitude: result.longitude,
          }))}
          required
        />

        <FormInputField
          label="Hourly Rate (€)"
          type="number"
          value={formData.hourlyRate.toString()}
          onChange={(value) => setFormData({ ...formData, hourlyRate: parseFloat(value) || 0 })}
          min="0"
          step="0.01"
          placeholder="15.00"
          required
        />

        <FormSelectField
          label="Frequency"
          value={formData.recurrenceFrequency}
          onChange={(value) => setFormData({ ...formData, recurrenceFrequency: value as RecurrenceFrequency })}
          options={FREQUENCY_OPTIONS}
        />

        {/* Days of Week Selection */}
        <div style={FORM_GROUP_STYLE}>
          <label style={LABEL_STYLE}>
            Days of Week <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid',
                  borderColor: formData.daysOfWeek.includes(day.value) ? '#3b82f6' : '#d1d5db',
                  borderRadius: '6px',
                  backgroundColor: formData.daysOfWeek.includes(day.value) ? '#3b82f6' : 'white',
                  color: formData.daysOfWeek.includes(day.value) ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>

        {/* Time Selection - Two columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={FORM_GROUP_STYLE}>
            <label style={LABEL_STYLE}>
              Start Time <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              style={INPUT_STYLE}
              required
            />
          </div>
          <div style={FORM_GROUP_STYLE}>
            <label style={LABEL_STYLE}>
              End Time <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              style={INPUT_STYLE}
              required
            />
            {formData.startTime && formData.endTime && formData.endTime < formData.startTime && (
              <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                Overnight shift — ends the following day
              </span>
            )}
          </div>
        </div>

        {/* Effective Until */}
        <FormInputField
          label="Effective Until"
          type="date"
          value={formData.effectiveUntil}
          onChange={(value) => setFormData({ ...formData, effectiveUntil: value })}
          helperText="Leave empty for indefinite"
        />

        {error && <ErrorMessage message={error} />}

        <ModalButtonGroup
          onCancel={onClose}
          submitLabel="Save Changes"
          isLoading={loading}
          loadingLabel="Saving..."
        />
      </form>
    </BaseModal>

    {confirmDialog && (
      <ConfirmationDialog
        isOpen={true}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Continue"
        confirmStyle="warning"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    )}
    </>
  );
}
